import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../api/services/auth.service';

/**
 * Role-aware route protection.
 *
 * authGuard only ever asked "is anyone signed in?", so every bar-staff screen
 * and the admin dashboard were reachable by ANY authenticated user simply by
 * typing the address — a customer included. The selection screen hid those
 * destinations behind a choice, but hiding a link is not access control.
 *
 * The user's kind comes from the `user_type` claim the authorization server
 * puts in the token: CUSTOMER | BAR_STAFF | PLATFORM_ADMIN.
 *
 * This is the browser's half only. It stops a customer wandering into screens
 * that are not theirs; it is NOT the security boundary. Every service still
 * authorises each call on its own (@PreAuthorize plus the bar_id claim), which
 * is what actually protects the data.
 */
function guardFor(allowed: string[], label: string): CanActivateFn {
  return async (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const auth = inject(AuthService);
    const router = inject(Router);

    let type = await auth.getCurrentUserType().catch(() => null);

    // One retry on a clean cache before concluding they are signed out.
    //
    // The claim is read from the access token, and the app caches that token.
    // If the cached copy has expired — or the one fetch it makes happens to
    // fail — this guard saw "no role" and threw a perfectly valid session out
    // to the sign-in page, mid-task. Clearing the cache makes the next call
    // fetch a fresh token from the session the browser still holds.
    if (!type) {
      auth.clearTokenCache();
      type = await auth.getCurrentUserType().catch(() => null);
    }

    if (!type) {
      // Not signed in at all: keep where they were headed so they land there
      // after logging in.
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    if (allowed.includes(type)) return true;

    // Signed in, but this is not their part of the product. Send them to their
    // own home rather than the login page, which would look like a session
    // problem and invite them to sign in again to the same refusal.
    console.warn(`[roleGuard] ${type} may not open ${label} (${state.url})`);
    router.navigate([homeFor(type)]);
    return false;
  };
}

/** Where each kind of user belongs. */
export function homeFor(userType: string | null): string {
  switch (userType) {
    case 'BAR_STAFF':      return '/service-provider';
    case 'PLATFORM_ADMIN': return '/admin';
    case 'CUSTOMER':       return '/customer';
    default:               return '/';
  }
}

/** Bar owners and their staff. Platform admins may look too. */
export const staffGuard: CanActivateFn = guardFor(['BAR_STAFF', 'PLATFORM_ADMIN'], 'the bar dashboard');

/** Platform administrators only. */
export const adminGuard: CanActivateFn = guardFor(['PLATFORM_ADMIN'], 'the admin dashboard');

/** Signed-in customers (staff and admins are allowed to browse as customers). */
export const customerGuard: CanActivateFn =
  guardFor(['CUSTOMER', 'BAR_STAFF', 'PLATFORM_ADMIN'], 'the customer area');
